import { Elements } from "@stripe/react-stripe-js"
import { StripeElementsOptions } from "@stripe/stripe-js"
import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react"

import { StripeLoadError } from "@/utilities/expectedErrors"
import { STRIPE_PROMISE } from "@/utilities/globals"
import { useTheme } from "@/utilities/hooks/useTheme"
import {
  createStripeAppearance,
  stripeFonts,
} from "@/utilities/json/stripeTheme"
import { type Flow, log } from "@/utilities/logging"
import { collectStripeDiagnostics } from "@/utilities/stripe"

interface StripeElementsWrapperProps {
  clientSecret: string
  customerSessionClientSecret?: string
  children: ReactNode
  appearance?: StripeElementsOptions["appearance"]
  fonts?: StripeElementsOptions["fonts"]
  flow?: Flow
}

function StripeElementsWrapper(
  props: PropsWithChildren<StripeElementsWrapperProps>
) {
  const { clientSecret, customerSessionClientSecret, flow = "misc" } = props
  const { theme } = useTheme()
  const [stripePromiseError, setStripePromiseError] = useState(false)

  // Monitor the loadStripe() promise for failure.
  // loadStripe() resolves to null when the script fails to load (e.g. adblocker),
  // and rejects when there's a more fundamental error.
  useEffect(() => {
    let cancelled = false
    STRIPE_PROMISE.then((stripe) => {
      if (cancelled) return
      if (stripe === null) {
        log({
          level: "error",
          flow,
          scope: "misconfiguration",
          message:
            "loadStripe() resolved to null - Stripe SDK failed to load (likely blocked)",
          meta: collectStripeDiagnostics(),
          sentry: "exception",
          exception: new Error("loadStripe() resolved to null"),
        })
        setStripePromiseError(true)
      }
    }).catch((error) => {
      if (cancelled) return
      log({
        level: "error",
        flow,
        scope: "misconfiguration",
        message: "loadStripe() promise rejected - Stripe SDK failed to load",
        meta: { error, ...collectStripeDiagnostics() },
        sentry: "exception",
        exception: error instanceof Error ? error : new Error(String(error)),
      })
      setStripePromiseError(true)
    })
    return () => {
      cancelled = true
    }
  }, [flow])

  // Throw to error boundary if loadStripe() failed
  if (stripePromiseError) {
    throw new StripeLoadError()
  }

  // Memoize the appearance to avoid recreating on every render
  const stripeAppearance = useMemo(() => {
    // Use scanned area theme if available, otherwise use default Inflow styling (CSS variables)
    return createStripeAppearance(theme)
  }, [theme])

  const options: StripeElementsOptions = {
    clientSecret,
    customerSessionClientSecret,
    appearance: stripeAppearance,
    fonts: stripeFonts,
    loader: "auto",
  }

  // Create a key that changes when critical options change
  // This forces Elements to remount when auth state changes
  const elementsKey = `${clientSecret}-${customerSessionClientSecret || "no-customer"}`

  return (
    <Elements stripe={STRIPE_PROMISE} options={options} key={elementsKey}>
      {props.children}
    </Elements>
  )
}

export default StripeElementsWrapper
